import { BaseTrack, TrackConfig, TrackData } from '../base/base-track';
import { LinksDataProcessor } from './links-data';

export class LinksTrack extends BaseTrack {
  constructor(config: TrackConfig, data: TrackData) {
    super('links', { ...config, height: 80 }, data);
  }

  createHTML(): string {
    if (!this.hasRequiredData() || !this.data.notation?.basePairs?.length) {
      return '';
    }

    return `
      <div style="display: flex; align-items: center;">
        <div style="width: ${this.config.labelWidth}px; flex-shrink: 0;">
          Interactive secondary structure
        </div>
        <div style="position: relative; flex: 1; overflow: hidden;">
          <nightingale-links
            id="secondary-structure-links-track"
            style="width: 100%; height: 100%;">
          </nightingale-links>
        </div>
      </div>
    `;
  }

  setup(container: HTMLElement): void {
    this.element = container.querySelector('#secondary-structure-links-track') as HTMLElement;
    
    if (!this.element) {
      console.error('Links track element not found');
      return;
    }

    this.setupNightingaleLinks();
  }

  update(config?: Partial<TrackConfig>, data?: Partial<TrackData>): void {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    if (data) {
      this.data = { ...this.data, ...data };
    }
    
    if (this.element) {
      this.setupNightingaleLinks();
    }
  }

  destroy(): void {
    this.element = undefined;
  }

  private setupNightingaleLinks(): void {
    if (!this.element || !this.data.notation?.basePairs?.length) {
      console.log('No base pairs data available for links track');
      return;
    }

    const basePairs = this.data.notation.basePairs;

    const nightingaleAttributes = this.getNightingaleAttributes();
    this.applyAttributes(this.element, {
      ...nightingaleAttributes,
      'height': this.config.height.toString(),
      'min-distance': '0',  // Don't filter by distance
      'min-probability': '0'  // Don't filter by probability
    });

    // Get the formatted data for nightingale-links
    const dataFormats = LinksDataProcessor.formatForNightingale(basePairs);
    const formatNames = ['string', 'array', 'contactObject'];

    // Try each format
    for (let i = 0; i < dataFormats.length; i++) {
      try {
        // Use the 'contacts' property which is what nightingale-links expects
        (this.element as any).contacts = dataFormats[i];
        break;
      } catch (error) {
        console.log(`Links ${formatNames[i]} format failed:`, error);
      }
    }

    this.setupPermanentArcs();
  }

  private setupPermanentArcs(): void {
    const linksElement = this.element as any;

    const drawPermanentArcs = () => {
      if (!linksElement._linksData || !linksElement.seqG) return;
      const linksGroup = linksElement.seqG.select('g.links-group');
      linksGroup.selectAll('.contact-link-permanent').remove();
      linksElement._linksData.forEach((link: number[]) => {
        linksGroup.append('path')
          .attr('class', 'contact-link-permanent')
          .attr('fill', 'transparent')
          .attr('stroke', 'rgba(70, 130, 180, 0.5)')
          .attr('stroke-width', 1)
          .style('pointer-events', 'none')
          .attr('d', linksElement.arc(link));
      });
    };

    // Override refreshSelected to always redraw permanent arcs after each interaction
    const originalRefreshSelected = linksElement.refreshSelected.bind(linksElement);
    linksElement.refreshSelected = function () {
      originalRefreshSelected();
      drawPermanentArcs();
    };

    // Draw arcs immediately after data is set
    setTimeout(drawPermanentArcs, 0);
  }
}